import {
  buildDayPlanWeatherWarnings,
  can,
  type MeResponse,
  PERMIT_KINDS,
  type PermitKind,
  type ProjectSummary,
  SITE_WEATHER_CONDITIONS,
  type SitePhotoLinks,
  type SiteProjectWeather,
  type SiteReportDraft,
  type SiteReportSnapshot,
  type SiteTelemetryReading,
  type SiteWeather,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useBlocker, useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowLeft, CheckCircle2, CloudUpload, FileText, Plus, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ZodError } from 'zod';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { DayFramesPanel } from './DayFramesPanel';
import { pendingCommand, SiteEditor } from './editor';
import {
  LineResources,
  type SiteMachineOption,
  type SiteMaterialOption,
  type SiteWarehouseOption,
  type SiteWorkerOption,
} from './LineResources';
import {
  applyPreviousReportSeed,
  type DayPackage,
  type DayPlan,
  dayDeviation,
  draftFromPackage,
  editableReport,
  type PreviousReportSeed,
  refreshDraftPlan,
  removeDraftLine,
  SITE_STATES,
} from './model';
import { askPhotoLocation } from './offline/photo-location';
import { type PhotoSource, photoMetadata, preparePhoto, withUploadedFileIds } from './offline/photo-sync';
import { saveSiteDraft } from './offline/queue';
import { registerOfflineShell } from './offline/register-sw';
import {
  siteDeviceScope,
  sitePhotoUploaderFor,
  siteReportLock,
  withSiteReportLock,
} from './offline/report-sync';
import { type LocalSiteReport, type OfflineScope, SiteOfflineStore } from './offline/store';
import { prepareVideo } from './offline/video-prepare';
import { PhotoStrip } from './PhotoStrip';
import { PostingStatus } from './PostingStatus';
import { reportResponse, siteTransport } from './transport';
import { emptyWeather, WEATHER_CONDITION_LABELS, weatherSummary } from './weather';

type Search = { project?: string; day?: string; draft?: string; report?: string };
const message = (error: unknown) => {
  if (error instanceof ZodError) {
    const issue = error.issues[0];
    return new Error(
      issue?.path.includes('projectTimeZone')
        ? 'Укажите действительный часовой пояс, например Asia/Qyzylorda.'
        : issue?.path.includes('claimedQuantity')
          ? `Работа ${Number(issue.path[1]) + 1}: введите неотрицательное количество с точкой или запятой, не более 6 знаков после неё.`
          : 'Проверьте дату, смену и заполнение работ перед отправкой. Ввод остаётся на устройстве.',
    );
  }
  return error instanceof Error ? error : new Error('Не удалось выполнить действие');
};
const today = () => new Date().toLocaleDateString('en-CA');
const quantityLabel = (value: string | null) =>
  value === null ? 'не задано' : value.includes('.') ? value.replace(/\.?0+$/, '') : value;

const scopeFor = siteDeviceScope;

export function SitePage() {
  const me = useMe();
  if (!me.tenant?.staff)
    return (
      <Page>
        <ErrorBox error={new Error('Площадка доступна сотрудникам компании')} />
      </Page>
    );
  return <SiteWorkspace key={`${me.tenant.id}:${me.user.id}`} me={me} />;
}

function SiteWorkspace({ me }: { me: MeResponse }) {
  const search = useSearch({ strict: false }) as Search;
  const navigate = useNavigate();
  const [storage, setStorage] = useState<{ store: SiteOfflineStore; scope: OfflineScope } | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [locals, setLocals] = useState<LocalSiteReport[]>([]);
  const [timeZone, setTimeZone] = useState('');
  const [shift, setShift] = useState('Дневная');
  const [selectedPackage, setSelectedPackage] = useState('');
  const [copyYesterday, setCopyYesterday] = useState(true);
  const [creating, setCreating] = useState(false);
  const day = search.day ?? today();
  const projects = useQuery({
    queryKey: ['site', me.tenant!.id, me.user.id, 'projects'],
    queryFn: () => api<ProjectSummary[]>('/projects'),
  });
  const project = projects.data?.find((p) => p.id === search.project);
  // Пояс места работ по умолчанию — пояс компании (тот же, по которому считается срок сдачи ПР-05):
  // прораб не обязан знать его название; для площадки в другом поясе поле можно исправить
  const companyClock = useQuery({
    queryKey: ['site', me.tenant!.id, 'report-deadline'],
    queryFn: () => api<{ timezone: string }>('/site/report-deadline'),
    staleTime: 60 * 60_000,
  });
  useEffect(() => {
    if (companyClock.data?.timezone) setTimeZone((tz) => tz || companyClock.data.timezone);
  }, [companyClock.data?.timezone]);
  const allowed = Boolean(
    project &&
      can(me.access, 'site.report', {
        projectId: project.id,
        legalEntityId: project.legalEntityId,
        directionId: project.directionId,
        projectPath: [project.parentId],
      }),
  );
  const plan = useQuery({
    queryKey: ['site', me.tenant!.id, me.user.id, 'plan', search.project, day],
    queryFn: () => api<DayPlan>(`/site/projects/${search.project}/day-plan`, { query: { date: day } }),
    enabled: Boolean(search.project),
    retry: false,
  });
  // ПР-16: погода плана дня отдельно от состава работ (не тормозит day-plan)
  const planWeather = useQuery({
    queryKey: ['site', me.tenant!.id, 'plan-weather', search.project, day],
    queryFn: () =>
      api<SiteProjectWeather>(`/site/projects/${search.project}/weather`, { query: { date: day } }),
    enabled: Boolean(search.project),
    retry: false,
  });
  const selectedPlan = plan.data?.packages.find((p) => p.packageId === selectedPackage);
  const planWeatherWarnings = useMemo(() => {
    const names = (plan.data?.packages ?? []).flatMap((p) => p.lines.map((l) => l.name));
    return buildDayPlanWeatherWarnings(planWeather.data?.weather, names);
  }, [plan.data?.packages, planWeather.data?.weather]);
  const go = (next: Search) => navigate({ to: '/site', search: next });
  useCrumbs([
    { label: project?.name ?? 'Отчёт смены', to: '/site' },
    ...(search.draft ? [{ label: 'Отчёт смены' }] : []),
  ]);
  useEffect(() => {
    let mounted = true;
    let opened: SiteOfflineStore | undefined;
    void (async () => {
      const fresh = await api<MeResponse>('/auth/me');
      if (fresh.user.id !== me.user.id || fresh.tenant?.id !== me.tenant!.id)
        throw new Error('Сессия изменилась. Обновите страницу.');
      const scope = scopeFor(fresh);
      opened = await SiteOfflineStore.open();
      if (mounted) setStorage({ store: opened, scope });
      else opened.close();
    })().catch((e) => mounted && setError(message(e)));
    return () => {
      mounted = false;
      opened?.close();
    };
  }, [me.user.id, me.tenant?.id]);
  const queryClient = useQueryClient();
  useEffect(() => {
    // Когда обработчик работы без связи готов, план дня читается ещё раз уже через него и запоминается
    void registerOfflineShell().then(async (state) => {
      if (state === 'ready')
        await queryClient.refetchQueries({ queryKey: ['site', me.tenant!.id, me.user.id], type: 'active' });
    });
  }, [queryClient, me.tenant, me.user.id]);
  useEffect(() => {
    if (storage && !search.draft)
      void storage.store
        .list(storage.scope)
        .then(setLocals)
        .catch((e) => setError(message(e)));
  }, [storage, search.draft]);
  async function create() {
    if (!storage || !project) return;
    setCreating(true);
    setError(null);
    try {
      const pkg = plan.data?.packages.find((p) => p.packageId === selectedPackage);
      if (!pkg) throw new Error('Выберите выданный пакет работ');
      if (!timeZone) throw new Error('Укажите часовой пояс места работ');
      let draft = draftFromPackage(project.id, day, pkg, timeZone, shift);
      if (copyYesterday) {
        try {
          const seed = await api<PreviousReportSeed | null>(
            `/site/projects/${project.id}/previous-report-seed`,
            { query: { date: day, packageId: pkg.packageId, shiftKey: shift.trim() } },
          );
          if (seed?.lines.length) draft = applyPreviousReportSeed(draft, pkg, seed);
        } catch {
          // Нет прошлой смены или нет связи — начинаем пустой отчёт.
        }
      }
      const clientId = crypto.randomUUID();
      await saveSiteDraft(storage.store, storage.scope, clientId, draft, 0, Date.now());
      await go({ project: project.id, day, draft: clientId });
    } catch (e) {
      setError(message(e));
    } finally {
      setCreating(false);
    }
  }
  if (search.report)
    return <ServerReport id={search.report} me={me} back={() => go({ project: search.project, day })} />;
  if (search.draft && storage)
    return (
      <ReportEditor
        key={search.draft}
        storage={storage}
        clientId={search.draft}
        allowed={allowed}
        pkg={plan.data?.packages}
        projectName={project?.name}
        refreshPlan={async () => {
          const result = await plan.refetch();
          if (result.error) throw result.error;
          return result.data!;
        }}
        back={() => go({ project: search.project, day })}
        showReport={(id) => go({ project: search.project, day, report: id })}
      />
    );
  return (
    <Page className="max-w-5xl">
      <PageHeader
        title="Отчёт смены"
        subtitle="План работ и отчёт за смену"
        actions={
          <Link className="inline-flex min-h-11 items-center underline" to="/site/day">
            Мой день: неделя, поставки, наряды, замечания
          </Link>
        }
      />
      <div className="grid gap-5 lg:grid-cols-[1.25fr_1fr]">
        <Panel title="План дня">
          <div className="grid gap-4">
            <Link
              className="inline-flex min-h-11 items-center underline"
              to="/site/summary"
              search={{ project: search.project, day } as never}
            >
              Сводка дня по проекту
            </Link>
            <Link
              className="inline-flex min-h-11 items-center underline"
              to="/site/extra-works"
              search={{ project: search.project, day } as never}
            >
              Акты на дополнительные работы
            </Link>
            <Link
              className="inline-flex min-h-11 items-center underline"
              to="/site/review"
              search={{ project: search.project }}
            >
              Проверка отправленных отчётов
            </Link>
            <Field label="Проект" htmlFor="site-project">
              <Select
                id="site-project"
                className="!h-11"
                value={search.project ?? ''}
                onChange={(e) => {
                  setSelectedPackage('');
                  void go({ project: e.target.value || undefined, day });
                }}
              >
                <option value="">Выберите проект</option>
                {projects.data?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Дата смены" htmlFor="site-day">
              <Input
                id="site-day"
                className="!h-11 min-w-0"
                type="date"
                value={day}
                onChange={(e) => {
                  if (e.target.value) {
                    setSelectedPackage('');
                    void go({ project: search.project, day: e.target.value });
                  }
                }}
              />
            </Field>
            {projects.error || plan.error ? <ErrorBox error={projects.error ?? plan.error} /> : null}
            {plan.isFetching ? <Spinner label="Читаем план работ" /> : null}
            {search.project && planWeather.data ? (
              <div className="rounded-md border border-line bg-surface-2 px-4 py-3 text-sm">
                <p className="font-medium text-ink">Погода · {planWeather.data.locationLabel}</p>
                <p className="mt-1 text-ink-2">{weatherSummary(planWeather.data.weather) ?? 'Нет данных'}</p>
                {planWeatherWarnings.length ? (
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-danger">
                    {planWeatherWarnings.map((w) => (
                      <li key={w.kind}>{w.message}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : search.project && planWeather.isError ? (
              <p className="text-sm text-ink-2">
                Погоду на день подставить не удалось — предупреждения по морозу и ветру недоступны.
              </p>
            ) : null}
            {search.project && plan.data && !plan.data.packages.length ? (
              <p className="text-sm text-ink-2">
                На эту дату вам не выдан пакет работ. Уточните план у руководителя проекта.
              </p>
            ) : null}
            {plan.data?.packages.length ? (
              <Field label="Пакет работ" htmlFor="site-package">
                <Select
                  id="site-package"
                  className="!h-11"
                  value={selectedPackage}
                  onChange={(e) => setSelectedPackage(e.target.value)}
                >
                  <option value="">Выберите пакет</option>
                  {plan.data.packages.map((p) => (
                    <option key={p.packageId} value={p.packageId}>
                      {p.name} · {p.lines.length} работ
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
            {selectedPackage ? (
              <>
                {selectedPlan ? (
                  <div className="grid gap-3 rounded-md border border-line p-4">
                    <h3 className="font-semibold">Работы пакета</h3>
                    {selectedPlan.lines.map((line) => (
                      <div key={line.packageItemId} className="min-w-0 text-sm">
                        <p className="font-medium [overflow-wrap:anywhere]">{line.name}</p>
                        <p className="mt-1 text-ink-2">
                          По графику: {quantityLabel(line.totalQuantity)} {line.unit ?? ''}
                          {' · '}Факт: {quantityLabel(line.actualQuantity)} {line.unit ?? ''}
                        </p>
                        {line.permit?.required ? (
                          <p className={`mt-1 ${line.permit.ok ? 'text-ink-2' : 'text-danger'}`}>
                            {line.permit.ok && line.permit.permit
                              ? `Наряд ${line.permit.permit.number}${
                                  line.permit.kind
                                    ? ` · ${PERMIT_KINDS[line.permit.kind as PermitKind]?.title ?? line.permit.kind}`
                                    : ''
                                }`
                              : (line.permit.reason ?? 'Нет действующего наряда-допуска')}
                          </p>
                        ) : null}
                      </div>
                    ))}
                    <p className="text-sm text-ink-2">
                      Объём на смену не распределён. В отчёте укажите выполненное за эту смену.
                    </p>
                  </div>
                ) : null}
                <Field label="Смена" htmlFor="site-shift">
                  <Input
                    id="site-shift"
                    className="!h-11"
                    maxLength={80}
                    value={shift}
                    onChange={(e) => setShift(e.target.value)}
                  />
                </Field>
                <Field
                  label="Часовой пояс места работ"
                  htmlFor="site-timezone"
                  hint="Подставлен пояс компании. Если площадка в другом поясе — исправьте; пояс телефона не используется."
                >
                  <Input
                    id="site-timezone"
                    className="!h-11"
                    placeholder="Например, Asia/Qyzylorda"
                    value={timeZone}
                    onChange={(e) => setTimeZone(e.target.value)}
                  />
                </Field>
                <label className="flex min-h-11 cursor-pointer items-start gap-3 text-sm text-ink">
                  <input
                    type="checkbox"
                    className="mt-1 size-5 shrink-0"
                    checked={copyYesterday}
                    onChange={(e) => setCopyYesterday(e.target.checked)}
                  />
                  <span>
                    Скопировать людей и технику из прошлой смены того же пакета (объёмы и фото — заново).
                  </span>
                </label>
                <Button
                  variant="primary"
                  size="lg"
                  icon={<Plus className="size-4" />}
                  disabled={!storage || !allowed || !plan.data?.projectActive || !timeZone || !shift.trim()}
                  loading={creating}
                  onClick={() => void create()}
                >
                  Начать отчёт
                </Button>
                {!allowed ? (
                  <p className="text-sm text-ink-2">
                    Для создания отчёта нужно право на отчёты этого проекта.
                  </p>
                ) : null}
                {plan.data && !plan.data.projectActive ? (
                  <p className="text-sm text-danger">Проект закрыт для новых отчётов.</p>
                ) : null}
              </>
            ) : null}
            {error ? <ErrorBox error={error} /> : null}
          </div>
        </Panel>
        <Panel title="На этом устройстве" meta="Ваши отчёты">
          <p className="mb-4 text-sm text-ink-2">
            Черновики восстанавливаются после входа тем же пользователем. Для открытия портала заново нужна
            сеть.
          </p>
          <div className="grid gap-3">
            {locals
              .filter((r) => !search.project || (r.draft as SiteReportDraft).projectId === search.project)
              .map((row) => {
                const draft = row.draft as SiteReportDraft;
                const snapshot = row.serverSnapshot as SiteReportSnapshot | null;
                return (
                  <button
                    key={row.clientId}
                    type="button"
                    className="rounded-md border border-line p-4 text-left hover:bg-surface-2"
                    onClick={() =>
                      void go({ project: draft.projectId, day: draft.reportDay, draft: row.clientId })
                    }
                  >
                    <span className="block font-semibold">
                      {draft.reportDay} · {draft.shiftKey}
                    </span>
                    <span className="mt-1 block text-sm text-ink-2">
                      {draft.lines.length} работ · {snapshot ? SITE_STATES[snapshot.state] : 'На устройстве'}
                    </span>
                    {pendingCommand(row) ? (
                      <span className="mt-1 block text-sm text-warning">
                        {pendingCommand(row)?.state === 'conflict'
                          ? 'Нужно сравнить версии'
                          : 'Есть неотправленные изменения'}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            {!locals.length ? (
              <p className="text-sm text-ink-2">Здесь появятся созданные вами отчёты.</p>
            ) : null}
          </div>
        </Panel>
      </div>
    </Page>
  );
}

function ReportEditor({
  storage,
  clientId,
  allowed,
  pkg: packages,
  projectName,
  back,
  showReport,
  refreshPlan,
}: {
  storage: { store: SiteOfflineStore; scope: OfflineScope };
  clientId: string;
  allowed: boolean;
  pkg?: DayPackage[];
  projectName?: string;
  back: () => unknown;
  showReport: (id: string) => unknown;
  refreshPlan: () => Promise<DayPlan>;
}) {
  const editor = useMemo(
    () => new SiteEditor(storage.store, storage.scope, clientId, siteTransport(storage.scope)),
    [storage, clientId],
  );
  const [row, setRow] = useState<LocalSiteReport | null>(null);
  const [draft, setDraft] = useState<SiteReportDraft | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(0);
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const unsaved = saving > 0 || Boolean(row && draft && JSON.stringify(row.draft) !== JSON.stringify(draft));
  const alive = useRef(true);
  const currentDraft = useRef(draft);
  currentDraft.current = draft;
  const projectId = draft?.projectId;
  const fleet = useQuery({
    queryKey: ['site', 'machines', projectId],
    queryFn: () => api<SiteMachineOption[]>(`/site/projects/${projectId}/machines`),
    enabled: Boolean(projectId),
    retry: false,
  });
  const crew = useQuery({
    queryKey: ['site', 'workers', projectId],
    queryFn: () => api<SiteWorkerOption[]>(`/site/projects/${projectId}/workers`),
    enabled: Boolean(projectId),
    retry: false,
  });
  const stock = useQuery({
    queryKey: ['site', 'materials', projectId],
    queryFn: () =>
      api<{ warehouses: SiteWarehouseOption[]; items: SiteMaterialOption[] }>(
        `/site/projects/${projectId}/materials`,
      ),
    enabled: Boolean(projectId),
    retry: false,
  });
  const reportDay = draft?.reportDay;
  const telemetryQ = useQuery({
    queryKey: ['site', 'telemetry', projectId, reportDay],
    queryFn: () =>
      api<SiteTelemetryReading[]>(`/site/projects/${projectId}/telemetry`, {
        query: { date: reportDay! },
      }),
    enabled: Boolean(projectId && reportDay),
    retry: false,
  });
  const photoLinks = useQuery({
    queryKey: ['site', 'photo-links', projectId],
    queryFn: () => api<SitePhotoLinks>(`/site/projects/${projectId}/photo-links`),
    enabled: Boolean(projectId),
    retry: false,
  });
  const machineUnits = fleet.data ?? [];
  const workerUnits = crew.data ?? [];
  const warehouseUnits = stock.data?.warehouses ?? [];
  const materialUnits = stock.data?.items ?? [];
  const telemetryUnits = telemetryQ.data ?? [];
  const sheetOptions = photoLinks.data?.sheets ?? [];
  const remarkOptions = photoLinks.data?.remarks ?? [];
  useBlocker({
    shouldBlockFn: () =>
      unsaved && !window.confirm('Изменения ещё не сохранены на устройстве. Уйти и потерять этот ввод?'),
    enableBeforeUnload: unsaved,
  });
  useEffect(() => {
    alive.current = true;
    void editor
      .load()
      .then(async (r) => {
        if (alive.current) {
          if (!r) throw new Error('Черновик не найден на этом устройстве');
          setRow(r);
          setDraft(r.draft as SiteReportDraft);
          if (
            r.serverId &&
            !pendingCommand(r) &&
            r.localRevision === r.syncedLocalRevision &&
            !editableReport(r.serverSnapshot as SiteReportSnapshot, editor.scope.userId)
          ) {
            const loadedDraft = r.draft;
            const report = reportResponse.parse(
              (await api<{ report: unknown }>(`/site/reports/${r.serverId}`)).report,
            ) as SiteReportSnapshot;
            const fresh = await editor.adoptServer(report);
            if (alive.current) {
              setRow(fresh);
              // Не заменять ввод, начатый во время GET.
              setDraft((current) =>
                JSON.stringify(current) === JSON.stringify(loadedDraft)
                  ? (fresh.draft as SiteReportDraft)
                  : current,
              );
            }
          }
        }
      })
      .catch((e) => setError(message(e)));
    return () => {
      alive.current = false;
    };
  }, [editor]);
  useEffect(() => {
    const reconnect = () => setOnline(navigator.onLine);
    window.addEventListener('online', reconnect);
    window.addEventListener('offline', reconnect);
    return () => {
      window.removeEventListener('online', reconnect);
      window.removeEventListener('offline', reconnect);
    };
  }, []);
  const sourcesRef = useRef<DayPackage['sourceRevisions']>([]);
  useEffect(() => {
    let running = false;
    let holdUntil = 0;
    const tick = () => {
      const current = editor.row;
      if (!navigator.onLine || !current || current.authPaused || running || Date.now() < holdUntil) return;
      const waiting = pendingCommand(current)?.state === 'pending';
      if (!waiting && !current.autoSend && !editor.photosWaiting()) return;
      running = true;
      // Та же блокировка, что у фоновой отправки с «Моего дня»: отчёт не отправляют двое сразу
      void withSiteReportLock(siteReportLock(editor.scope, editor.clientId), () =>
        editor.advance(sourcesRef.current, sitePhotoUploaderFor(editor.scope)),
      )
        .then((r) => {
          if (!alive.current || !r) return;
          // Отказ сервера или вход заново: тот же файл не гоним каждые 3 секунды
          if (r.photos.stopped === 'refused') holdUntil = Date.now() + 5 * 60_000;
          if (r.photos.stopped === 'auth') holdUntil = Date.now() + 30_000;
          setRow(r.row);
          // Фото загрузилось в фоне: его fileId — и в черновик на экране, иначе черновик на экране навсегда
          // «отличается» от сохранённого и кнопка «Сохранить на сервере» не включается
          setDraft((d) => (d ? withUploadedFileIds(d, r.row.photos) : d));
          setPhotoNote(
            r.photos.stopped === 'refused'
              ? 'Сервер не принял фото. Уберите его и снимите ещё раз.'
              : r.photos.stopped === 'auth'
                ? 'Войдите в портал заново: фото ждут на телефоне.'
                : null,
          );
        })
        .catch((e) => alive.current && setError(message(e)))
        .finally(() => {
          running = false;
        });
    };
    const timer = setInterval(tick, 3000);
    window.addEventListener('online', tick);
    window.addEventListener('focus', tick);
    tick();
    return () => {
      clearInterval(timer);
      window.removeEventListener('online', tick);
      window.removeEventListener('focus', tick);
    };
  }, [editor]);
  const [photoNote, setPhotoNote] = useState<string | null>(null);
  const addPhoto = (lineId: string, file: File, source: PhotoSource) => {
    const base = currentDraft.current;
    if (!base) return;
    setError(null);
    setSaving((n) => n + 1);
    void (async () => {
      const isVideo = file.type.startsWith('video/');
      const prepared = isVideo
        ? { ...(await prepareVideo(file, source, new Date())), kind: 'video' as const }
        : { ...(await preparePhoto(file, source, new Date())), kind: 'photo' as const, mimeType: null };
      // Место — только с камеры и только с согласия (МП-07); для фото и видео одинаково.
      const geo =
        source === 'camera'
          ? await askPhotoLocation((msg) => window.confirm(msg), undefined, isVideo ? 'video' : 'photo')
          : { locationConsent: false, location: null };
      const id = crypto.randomUUID();
      // Черновик берётся после подготовки: за это время текст мог измениться
      const latest = currentDraft.current ?? base;
      if (!latest.lines.some((line) => line.id === lineId)) return;
      // Тот же снимок второй раз не добавляется (ПР-03): иначе в отчёте был бы дубль
      if (latest.photos.some((p) => p.sha256 === prepared.sha256)) {
        if (alive.current) setPhotoNote('Этот снимок уже есть в отчёте — второй раз он не добавляется');
        return;
      }
      const next = {
        ...latest,
        photos: [...latest.photos, photoMetadata(id, lineId, source, prepared, '', geo)],
      };
      setDraft(next);
      currentDraft.current = next;
      const saved = await editor.addPhoto(next, id, prepared.blob);
      if (alive.current) {
        setRow(saved);
        if (geo.locationConsent && geo.location)
          setPhotoNote(
            isVideo ? 'Место съёмки сохранено в записи видео' : 'Место съёмки сохранено в записи фото',
          );
        else if (isVideo) setPhotoNote('Видео сохранено в черновике, уйдёт при появлении связи');
      }
    })()
      .catch((e) => alive.current && setError(message(e)))
      .finally(() => alive.current && setSaving((n) => n - 1));
  };
  /** Пометка на снимке до отправки: новый JPEG и sha256, тот же clientPhotoId. */
  const markPhoto = (clientPhotoId: string, prepared: { blob: Blob; sha256: string; size: number }) => {
    const base = currentDraft.current;
    if (!base) return;
    setError(null);
    setSaving((n) => n + 1);
    void (async () => {
      const latest = currentDraft.current ?? base;
      const next = {
        ...latest,
        photos: latest.photos.map((p) =>
          p.clientPhotoId === clientPhotoId && !p.fileId
            ? { ...p, sha256: prepared.sha256, size: prepared.size }
            : p,
        ),
      };
      setDraft(next);
      currentDraft.current = next;
      const saved = await editor.replacePhoto(next, clientPhotoId, prepared.blob);
      if (alive.current) setRow(saved);
    })()
      .catch((e) => alive.current && setError(message(e)))
      .finally(() => alive.current && setSaving((n) => n - 1));
  };
  const update = (next: SiteReportDraft) => {
    // Человек продолжил правку: автоотправка снимается, чтобы не уйти недописанным
    if (editor.row?.autoSend) void editor.setAutoSend(false).then((r) => alive.current && setRow(r));
    setDraft(next);
    currentDraft.current = next;
    setError(null);
    setSaving((n) => n + 1);
    void editor
      .save(next)
      .then((r) => alive.current && setRow(r))
      .catch((e) => alive.current && setError(message(e)))
      .finally(() => alive.current && setSaving((n) => n - 1));
  };
  async function run(fn: () => Promise<LocalSiteReport>) {
    const startingDraft = currentDraft.current;
    setBusy(true);
    setError(null);
    try {
      const result = await fn();
      if (alive.current) {
        setRow(result);
        // Фото, загруженное в фоне во время действия, добавляет fileId в черновик на экране — это не правка
        // человека: сравнение без учёта fileId, иначе ответ сервера отбрасывается и кнопки не включаются
        const same = (d: SiteReportDraft | null) =>
          JSON.stringify(d ? withUploadedFileIds(d, result.photos) : d);
        setDraft((current) =>
          same(current) === same(startingDraft) ? (result.draft as SiteReportDraft) : current,
        );
      }
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  if (!row || !draft)
    return (
      <Page>
        {error ? <ErrorBox error={error} /> : <Spinner label="Восстанавливаем черновик" />}
        <Button onClick={() => back()}>К плану дня</Button>
      </Page>
    );
  const snapshot = row.serverSnapshot as SiteReportSnapshot | null;
  const command = pendingCommand(row);
  const editable =
    allowed &&
    editableReport(snapshot, storage.scope.userId) &&
    !busy &&
    !(command?.action === 'submit' && command.state === 'pending');
  const pkg = packages?.find((p) => p.packageId === draft.packageId);
  const sources = pkg?.sourceRevisions ?? [];
  sourcesRef.current = sources;
  const clean = row.serverId && row.syncedLocalRevision === row.localRevision && !unsaved;
  const conflict = command?.conflict?.server as SiteReportSnapshot | undefined;
  const canResolve = editable && conflict && ['draft', 'returned'].includes(conflict.state);
  const savedText = saving
    ? 'Сохраняется на устройстве…'
    : error
      ? 'Проверьте ошибку сохранения'
      : 'Сохранено на устройстве';
  const photosLeft = editor.photosWaiting();
  // Без связи или пока фото не на сервере отправка идёт сама: фото → сохранение → проверка
  const useAutoSend = draft.lines.length > 0 && (!online || photosLeft > 0);
  async function sendWhenReady() {
    await run(async () => {
      const marked = await editor.setAutoSend(true);
      if (!navigator.onLine) return marked;
      return (await editor.advance(sourcesRef.current, sitePhotoUploaderFor(editor.scope))).row;
    });
  }
  async function action() {
    await run(async () => {
      await editor.command(clean ? 'submit' : 'save', sources);
      return navigator.onLine ? editor.sync() : editor.row!;
    });
  }
  return (
    <Page className="max-w-5xl">
      <Button
        variant="ghost"
        className="mb-3 !h-11"
        icon={<ArrowLeft className="size-4" />}
        disabled={unsaved || busy}
        onClick={() => back()}
      >
        К плану дня
      </Button>
      <PageHeader
        title="Отчёт смены"
        subtitle={`${projectName ?? 'Проект'} · ${draft.reportDay} · ${draft.shiftKey}`}
        actions={
          <Badge tone={snapshot?.state === 'accepted' ? 'success' : 'neutral'}>
            {snapshot ? SITE_STATES[snapshot.state] : 'На устройстве'}
          </Badge>
        }
      />
      <div
        className="mb-5 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line bg-surface p-4 text-sm"
        role="status"
        aria-live="polite"
      >
        <span>{savedText}</span>
        <span className="text-ink-2">
          {online ? 'Есть связь' : 'Нет связи · ввод сохраняется локально'}
          {row.serverId ? ` · серверная версия ${row.revision}` : ''}
        </span>
      </div>
      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}
      {row.authPaused ? (
        <Panel title="Нужно подтвердить вход" className="mb-4">
          <p className="mb-3 text-sm">
            Команда сохранена. Войдите тем же пользователем и продолжите отправку.
          </p>
          <Button
            onClick={() =>
              void run(async () => {
                await editor.resume();
                return editor.sync();
              })
            }
          >
            Проверить вход и повторить
          </Button>
          <a
            href={`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}
            className="ml-3 inline-flex min-h-11 items-center underline"
          >
            Войти
          </a>
        </Panel>
      ) : null}
      {command?.state === 'pending' ? (
        <Panel title="Ожидает подтверждения сервера" className="mb-4">
          <p className="text-sm">
            {command.attempts
              ? 'Ответ ещё не подтверждён. Повтор использует ту же команду и не создаёт второй отчёт.'
              : 'Команда сохранена на устройстве и отправится при восстановлении связи в открытом портале.'}
          </p>
          <Button
            className="mt-3 !h-11"
            disabled={!online || busy}
            onClick={() => void run(() => editor.sync())}
          >
            Проверить отправку
          </Button>
        </Panel>
      ) : null}
      {command?.state === 'rejected' ? (
        <Panel title="Отправка отклонена" className="mb-4">
          <p className="text-sm text-danger">{command.error?.message}</p>
          <p className="mt-2 text-sm">Ввод сохранён. Исправьте причину перед новой отправкой.</p>
          <Button
            className="mt-3 !h-11"
            onClick={() =>
              void (async () => {
                try {
                  const fresh = await refreshPlan();
                  const p = fresh.packages.find((x) => x.packageId === draft.packageId);
                  if (!p) throw new Error('Пакет больше не доступен');
                  update(refreshDraftPlan(currentDraft.current!, p));
                } catch (e) {
                  setError(message(e));
                }
              })()
            }
          >
            Перечитать и подтвердить план
          </Button>
        </Panel>
      ) : null}
      {conflict ? (
        <Panel title="Отчёт изменён в другом месте" className="mb-5">
          <p className="mb-4 text-sm">
            Оба варианта сохранены. Сравните объёмы и комментарии перед решением.
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <Version title="На устройстве" draft={draft} pkg={pkg} />
            <Version title={`На сервере · версия ${conflict.revision}`} draft={conflict.draft} pkg={pkg} />
          </div>
          {canResolve ? (
            <div className="mt-4 flex flex-wrap gap-3">
              <Button
                className="!h-11"
                onClick={() =>
                  void run(async () => {
                    const r = await editor.resolve(draft, sources);
                    setDraft(r.draft as SiteReportDraft);
                    return r;
                  })
                }
              >
                Сохранить мой вариант
              </Button>
              <Button
                className="!h-11"
                onClick={() =>
                  void run(async () => {
                    const r = await editor.resolve(conflict.draft, sources);
                    setDraft(r.draft as SiteReportDraft);
                    return r;
                  })
                }
              >
                Использовать серверный
              </Button>
            </div>
          ) : (
            <p className="mt-3 text-sm">
              Серверный отчёт закрыт для изменений. Ваш вариант остаётся на устройстве.
            </p>
          )}
        </Panel>
      ) : null}
      <div className="grid gap-5 lg:grid-cols-[1.6fr_1fr]">
        <Panel title="Выполненные работы" meta={`${draft.lines.length} в отчёте`}>
          <div className="grid gap-4">
            {draft.lines.map((line, index) => {
              const planLine = pkg?.lines.find((p) => p.packageItemId === line.packageItemId);
              const linePermit = planLine?.permit;
              return (
                <section key={line.id} className="rounded-md border border-line p-4">
                  <h3 className="mb-1 font-semibold [overflow-wrap:anywhere]">
                    {planLine?.name ?? `Работа ${index + 1}`}
                  </h3>
                  <p className="mb-3 text-sm text-ink-2">
                    {planLine?.plannedQuantity
                      ? `План на день по сменному заданию: ${planLine.plannedQuantity.replace('.', ',')} ${line.unit}`
                      : `План смены не задан · единица: ${line.unit}`}
                  </p>
                  {linePermit?.required && linePermit.ok && linePermit.permit ? (
                    <p className="mb-3 text-sm text-ink-2">
                      Наряд {linePermit.permit.number}
                      {linePermit.kind
                        ? ` · ${PERMIT_KINDS[linePermit.kind as PermitKind]?.title ?? linePermit.kind}`
                        : ''}
                      {linePermit.requires.length
                        ? ' · у людей нужны действующие удостоверения по наряду'
                        : ''}
                    </p>
                  ) : null}
                  {linePermit?.required && !linePermit.ok ? (
                    <p className="mb-3 text-sm text-danger">
                      {linePermit.reason ?? 'Нет действующего наряда-допуска'}
                    </p>
                  ) : null}
                  <Field label={`Выполнено за смену, ${line.unit}`} htmlFor={`quantity-${line.id}`}>
                    <Input
                      className="!h-12"
                      id={`quantity-${line.id}`}
                      inputMode="decimal"
                      value={line.claimedQuantity}
                      disabled={!editable || command?.state === 'conflict'}
                      onChange={(e) =>
                        update({
                          ...draft,
                          lines: draft.lines.map((x) =>
                            x.id === line.id
                              ? { ...x, claimedQuantity: e.target.value.replace(',', '.') }
                              : x,
                          ),
                        })
                      }
                    />
                  </Field>
                  {(() => {
                    const dev = dayDeviation(
                      line.plannedQuantity ?? planLine?.plannedQuantity ?? null,
                      line.claimedQuantity,
                      line.unit,
                    );
                    return dev ? (
                      <p
                        className={`mt-1 text-sm ${dev.over === false ? 'text-danger' : 'text-ink-2'}`}
                        data-testid={`deviation-${line.id}`}
                      >
                        {dev.text}
                        {dev.over === false ? ' — объясните в комментарии, что помешало' : ''}
                      </p>
                    ) : null;
                  })()}
                  <Field label="Комментарий к работе" htmlFor={`note-${line.id}`} className="mt-3">
                    <Textarea
                      id={`note-${line.id}`}
                      maxLength={2000}
                      value={line.note}
                      disabled={!editable}
                      onChange={(e) =>
                        update({
                          ...draft,
                          lines: draft.lines.map((x) =>
                            x.id === line.id ? { ...x, note: e.target.value } : x,
                          ),
                        })
                      }
                    />
                  </Field>
                  <LineResources
                    draft={draft}
                    line={line}
                    editable={Boolean(editable && command?.state !== 'conflict')}
                    machines={machineUnits}
                    workers={workerUnits}
                    warehouses={warehouseUnits}
                    materialItems={materialUnits}
                    telemetry={telemetryUnits}
                    onChange={update}
                  />
                  <PhotoStrip
                    lineId={line.id}
                    photos={withUploadedFileIds(draft, row.photos).photos}
                    local={row.photos}
                    editable={editable && command?.state !== 'conflict'}
                    online={online}
                    sheets={sheetOptions}
                    remarks={remarkOptions}
                    onAdd={addPhoto}
                    onCaption={(id, caption) =>
                      update({
                        ...draft,
                        photos: draft.photos.map((p) => (p.clientPhotoId === id ? { ...p, caption } : p)),
                      })
                    }
                    onLink={(id, link) =>
                      update({
                        ...draft,
                        photos: draft.photos.map((p) =>
                          p.clientPhotoId === id
                            ? { ...p, sheetId: link.sheetId, remarkId: link.remarkId }
                            : p,
                        ),
                      })
                    }
                    onForgetLocation={(id) =>
                      update({
                        ...draft,
                        photos: draft.photos.map((p) =>
                          p.clientPhotoId === id ? { ...p, locationConsent: false, location: null } : p,
                        ),
                      })
                    }
                    onMarkup={markPhoto}
                    onRemove={(id) =>
                      update({ ...draft, photos: draft.photos.filter((p) => p.clientPhotoId !== id) })
                    }
                  />
                  {editable ? (
                    <Button
                      className="mt-3 !h-11"
                      disabled={saving > 0 || command?.state === 'conflict'}
                      onClick={() => {
                        if (
                          window.confirm(
                            'Удалить эту работу из черновика? Её объём, ресурсы, комментарий и связи с фото будут убраны из отчёта. Сами файлы не удаляются.',
                          )
                        )
                          update(removeDraftLine(currentDraft.current!, line.id));
                      }}
                    >
                      Удалить работу из черновика
                    </Button>
                  ) : null}
                </section>
              );
            })}
            {!draft.lines.length ? (
              <p className="text-sm text-ink-2">
                Пустой черновик можно сохранить на сервере при наличии связи. Для отправки на проверку
                добавьте работу из выданного пакета и укажите объём за эту смену.
              </p>
            ) : null}
            {editable && pkg ? (
              <Field label="Добавить работу из плана" htmlFor="site-add-line">
                <Select
                  className="!h-11"
                  id="site-add-line"
                  value=""
                  onChange={(e) => {
                    const p = pkg.lines.find((x) => x.packageItemId === e.target.value);
                    if (!p?.unit) return;
                    if (p.permit?.required && !p.permit.ok) {
                      toast(p.permit.reason ?? 'Нет действующего наряда-допуска на эту работу', 'danger');
                      return;
                    }
                    update({
                      ...draft,
                      lines: [
                        ...draft.lines,
                        {
                          id: crypto.randomUUID(),
                          activityId: p.activityId,
                          packageItemId: p.packageItemId,
                          unit: p.unit,
                          plannedQuantity: p.plannedQuantity,
                          claimedQuantity: '',
                          ruleRef: null,
                          stepIds: [],
                          labor: [],
                          machines: [],
                          materials: [],
                          note: '',
                        },
                      ],
                    });
                  }}
                >
                  <option value="">Выберите выполненную работу</option>
                  {pkg.lines
                    .filter((p) => !draft.lines.some((l) => l.packageItemId === p.packageItemId))
                    .map((p) => {
                      const blocked = Boolean(p.permit?.required && !p.permit.ok);
                      return (
                        <option key={p.packageItemId} value={p.packageItemId} disabled={!p.unit || blocked}>
                          {p.name}
                          {!p.unit
                            ? ' · единица не задана'
                            : blocked
                              ? ' · нет наряда-допуска'
                              : p.permit?.ok && p.permit.permit
                                ? ` · ${p.permit.permit.number}`
                                : ''}
                        </option>
                      );
                    })}
                </Select>
              </Field>
            ) : null}
            {editable && pkg?.lines.some((p) => !p.unit) ? (
              <p className="text-sm text-ink-2">
                У части работ не задана единица объёма — по ним объём не внести. Попросите руководителя
                проекта указать единицу в карточке работы графика.
              </p>
            ) : null}
          </div>
        </Panel>
        <div className="grid content-start gap-5">
          {projectId && reportDay ? <DayFramesPanel projectId={projectId} day={reportDay} /> : null}
          <WeatherPanel
            draft={draft}
            editable={editable}
            projectId={projectId}
            reportDay={reportDay}
            update={update}
          />
          <Panel title="Комментарий к смене">
            <Textarea
              aria-label="Комментарий к смене"
              maxLength={2000}
              value={draft.note}
              disabled={!editable}
              onChange={(e) => update({ ...draft, note: e.target.value })}
            />
          </Panel>
          <Panel title="Передача на проверку">
            <p className="mb-4 text-sm text-ink-2">
              {snapshot && !editableReport(snapshot, storage.scope.userId)
                ? 'Изменения закрыты. Откройте серверную карточку, чтобы увидеть результат проверки.'
                : 'Сначала сохраните отчёт на сервере, затем отправьте его ПТО. Черновик не является принятым объёмом.'}
            </p>
            {photosLeft ? (
              <p className="mb-3 text-sm text-ink-2">
                Фото ждут отправки: {photosLeft}.{' '}
                {online ? 'Загружаем.' : 'Они хранятся на телефоне и уйдут, когда появится связь.'}
              </p>
            ) : null}
            {photoNote ? <p className="mb-3 text-sm text-danger">{photoNote}</p> : null}
            {row.autoSend ? (
              <p className="mb-3 rounded-md border border-line bg-tile p-3 text-sm" role="status">
                Отчёт отправится сам, когда загрузятся фото и появится связь. Один раз, без дублей.
              </p>
            ) : null}
            {editable && command?.state !== 'conflict' && !row.autoSend ? (
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                icon={clean ? <CheckCircle2 className="size-4" /> : <CloudUpload className="size-4" />}
                disabled={Boolean(
                  unsaved ||
                    busy ||
                    command?.state === 'pending' ||
                    row.authPaused ||
                    !pkg ||
                    (!draft.lines.length && (clean || !online)),
                )}
                loading={busy}
                onClick={() => void (useAutoSend ? sendWhenReady() : action())}
              >
                {useAutoSend
                  ? 'Отправить, когда будет связь'
                  : clean
                    ? 'Отправить на проверку'
                    : 'Сохранить на сервере'}
              </Button>
            ) : null}
            {row.serverId ? (
              <Button
                className="mt-3 w-full !h-11"
                icon={<FileText className="size-4" />}
                disabled={unsaved || busy}
                onClick={() => showReport(row.serverId!)}
              >
                Открыть серверный отчёт
              </Button>
            ) : null}
            {row.serverId && !command && clean ? (
              <Button
                className="mt-3 w-full !h-11"
                disabled={unsaved || busy || !online}
                onClick={() =>
                  void run(async () => {
                    const report = reportResponse.parse(
                      (await api<{ report: unknown }>(`/site/reports/${row.serverId}`)).report,
                    ) as SiteReportSnapshot;
                    const next = await editor.adoptServer(report);
                    setDraft(next.draft as SiteReportDraft);
                    return next;
                  })
                }
              >
                Обновить состояние отчёта
              </Button>
            ) : null}
            <p className="mt-4 text-xs text-ink-2">
              {draft.projectTimeZone} · версия плана {draft.scheduleRevision}
            </p>
          </Panel>
        </div>
      </div>
    </Page>
  );
}

function Version({ title, draft, pkg }: { title: string; draft: SiteReportDraft; pkg?: DayPackage }) {
  return (
    <div className="min-w-0 rounded border border-line p-3">
      <h3 className="mb-2 font-semibold">{title}</h3>
      {draft.lines.map((l, i) => (
        <p key={l.id} className="mb-2 break-words text-sm">
          {pkg?.lines.find((p) => p.packageItemId === l.packageItemId)?.name ?? `Работа ${i + 1}`}:{' '}
          {l.claimedQuantity} {l.unit}
          {l.note ? ` · ${l.note}` : ''}
        </p>
      ))}
      <p className="whitespace-pre-wrap break-words text-sm">{draft.note || 'Без комментария'}</p>
    </div>
  );
}

function ServerReport({ id, me, back }: { id: string; me: MeResponse; back: () => unknown }) {
  const q = useQuery({
    queryKey: ['site', me.tenant!.id, me.user.id, 'report', id],
    queryFn: async () =>
      reportResponse.parse(
        (await api<{ report: unknown }>(`/site/reports/${id}`)).report,
      ) as SiteReportSnapshot,
    retry: false,
  });
  const posting = useQuery({
    queryKey: ['site', me.tenant!.id, me.user.id, 'posting', id, q.data?.revision],
    queryFn: () => api<{ schedule: string; economy: string }>(`/site/reports/${id}/posting-status`),
    enabled: Boolean(q.data),
    retry: false,
  });
  const plan = useQuery({
    queryKey: ['site', me.tenant!.id, me.user.id, 'report-plan', id, q.data?.revision],
    queryFn: () =>
      api<DayPlan>(`/site/projects/${q.data!.draft.projectId}/day-plan`, {
        query: { date: q.data!.draft.reportDay, packageId: q.data!.draft.packageId },
      }),
    enabled: Boolean(q.data),
    retry: false,
  });
  useCrumbs([{ label: 'Отчёт смены' }], q.error);
  const r = q.error ? undefined : q.data;
  return (
    <Page className="max-w-4xl">
      <Button
        variant="ghost"
        className="mb-3 !h-11"
        icon={<ArrowLeft className="size-4" />}
        onClick={() => back()}
      >
        К плану дня
      </Button>
      <PageHeader
        title="Отчёт на сервере"
        subtitle={r ? `${r.draft.reportDay} · ${r.draft.shiftKey} · версия ${r.revision}` : undefined}
        actions={
          <Button
            className="!h-11"
            icon={<RefreshCw className="size-4" />}
            onClick={() => {
              void q.refetch();
              void posting.refetch();
            }}
          >
            Обновить
          </Button>
        }
      />
      {q.error ? <ErrorBox error={q.error} /> : null}
      {q.isPending ? <Spinner /> : null}
      {r ? (
        <div className="grid gap-5">
          <Panel title={SITE_STATES[r.state]}>
            <Version title="Заявленный объём" draft={r.draft} pkg={plan.data?.packages[0]} />
            {r.verification ? (
              <div className="mt-4">
                <h3 className="font-semibold">Проверено ПТО</h3>
                {r.verification.map((v, i) => (
                  <p key={v.lineId} className="mt-2 text-sm">
                    Работа {i + 1}: {v.quantity} {r.draft.lines.find((l) => l.id === v.lineId)?.unit}
                    {v.reason ? ` · ${v.reason}` : ''}
                  </p>
                ))}
              </div>
            ) : null}
            {r.decision?.reason ? (
              <p className="mt-4 whitespace-pre-wrap text-sm">Причина решения: {r.decision.reason}</p>
            ) : null}
          </Panel>
          {posting.error ? <ErrorBox error={posting.error} /> : null}
          {r.state === 'accepted' ? (
            <Panel title="Отражение принятого отчёта">
              <PostingStatus data={posting.data} loading={posting.isPending} />
            </Panel>
          ) : null}
          <p className="text-sm text-ink-2">
            Ссылку на эту карточку можно передать сотруднику с доступом к проекту. Связанные замечания
            открываются только через раздел качества.
          </p>
        </div>
      ) : null}
    </Page>
  );
}

/** Погода смены: ручной ввод и подстановка из Open-Meteo (ПР-02). */
function WeatherPanel({
  draft,
  editable,
  projectId,
  reportDay,
  update,
}: {
  draft: SiteReportDraft;
  editable: boolean;
  projectId?: string;
  reportDay?: string;
  update: (next: SiteReportDraft) => void;
}) {
  const w: SiteWeather = draft.weather ?? emptyWeather();
  const setWeather = (next: SiteWeather) => {
    const empty =
      next.temperatureC === null && next.condition === null && next.windMs === null && !next.note?.trim();
    update({
      ...draft,
      weather: empty ? null : { ...next, note: next.note?.trim() ? next.note : null },
    });
  };
  const auto = useMutation({
    mutationFn: () =>
      api<SiteProjectWeather>(`/site/projects/${projectId}/weather`, {
        query: { date: reportDay! },
      }),
    onSuccess: (data) => {
      setWeather({
        ...data.weather,
        note: w.note,
      });
      toast(`Погода подставлена · ${data.locationLabel}`);
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Не удалось получить погоду', 'danger'),
  });
  return (
    <Panel
      title="Погода"
      actions={
        editable && projectId && reportDay ? (
          <Button
            size="sm"
            loading={auto.isPending}
            onClick={() => auto.mutate()}
            icon={<RefreshCw className="size-4" />}
          >
            Подставить
          </Button>
        ) : null
      }
    >
      <p className="mb-3 text-sm text-ink-2">
        Условия смены на площадке. «Подставить» берёт сводку Open-Meteo по адресу проекта (без адреса — Алматы
        для компаний KZ); значения можно поправить вручную.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Температура, °C" htmlFor="site-weather-t">
          <Input
            id="site-weather-t"
            className="!h-11"
            inputMode="numeric"
            disabled={!editable}
            value={w.temperatureC === null ? '' : String(w.temperatureC)}
            placeholder="Например, 12"
            onChange={(e) => {
              const raw = e.target.value.trim().replace(',', '.');
              if (!raw) return setWeather({ ...w, temperatureC: null });
              const n = Number(raw);
              if (!Number.isFinite(n)) return;
              setWeather({ ...w, temperatureC: Math.round(Math.min(60, Math.max(-60, n))) });
            }}
          />
        </Field>
        <Field label="Условия" htmlFor="site-weather-c">
          <Select
            id="site-weather-c"
            className="!h-11"
            disabled={!editable}
            value={w.condition ?? ''}
            onChange={(e) =>
              setWeather({
                ...w,
                condition: (e.target.value || null) as SiteWeather['condition'],
              })
            }
          >
            <option value="">Не указано</option>
            {SITE_WEATHER_CONDITIONS.map((c) => (
              <option key={c} value={c}>
                {WEATHER_CONDITION_LABELS[c]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Ветер, м/с" htmlFor="site-weather-wind">
          <Input
            id="site-weather-wind"
            className="!h-11"
            inputMode="numeric"
            disabled={!editable}
            value={w.windMs === null ? '' : String(w.windMs)}
            placeholder="Например, 5"
            onChange={(e) => {
              const raw = e.target.value.trim();
              if (!raw) return setWeather({ ...w, windMs: null });
              const n = Number(raw.replace(/\D/g, ''));
              if (!Number.isFinite(n)) return;
              setWeather({ ...w, windMs: Math.min(70, Math.max(0, Math.round(n))) });
            }}
          />
        </Field>
        <Field label="Комментарий к погоде" htmlFor="site-weather-note" className="sm:col-span-2">
          <Input
            id="site-weather-note"
            className="!h-11"
            disabled={!editable}
            maxLength={2000}
            value={w.note ?? ''}
            placeholder="Например, порывы мешают работе крана"
            onChange={(e) => setWeather({ ...w, note: e.target.value || null })}
          />
        </Field>
      </div>
    </Panel>
  );
}
